import { useEffect, useRef } from "react";
import { HubConnectionState } from "@microsoft/signalr";

const MAX_BACKOFF_MS = 60000;

/**
 * Mantiene vivo el hub /hubs/seat: reconexion tras abort/cierre y re-registro AddToEvent.
 * Usar con changeStatusHandler estable (useCallback).
 */
export function useSeatHubConnection(connection, changeStatusHandler) {
  const handlerRef = useRef(changeStatusHandler);
  handlerRef.current = changeStatusHandler;

  useEffect(() => {
    if (!connection) return;

    let cancelled = false;
    let reconnectTimerId = null;
    let intervalId = null;
    let attempt = 0;

    const invokeAddToEvent = async () => {
      const eventKey = window.redseats?.eventKey;
      if (!eventKey) return;
      await connection.invoke("AddToEvent", eventKey);
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearTimeout(reconnectTimerId);
      const delay = Math.min(MAX_BACKOFF_MS, 1500 * Math.pow(2, Math.min(attempt, 6)));
      reconnectTimerId = setTimeout(() => {
        connectIfNeeded();
      }, delay);
    };

    const connectIfNeeded = async () => {
      if (cancelled) return;
      try {
        if (
          connection.state === HubConnectionState.Connected ||
          connection.state === HubConnectionState.Connecting ||
          connection.state === HubConnectionState.Reconnecting
        ) {
          return;
        }
        await connection.start();
        await invokeAddToEvent();
        attempt = 0;
      } catch (e) {
        console.log("Seat hub connection failed:", e);
        attempt += 1;
        scheduleReconnect();
      }
    };

    const wrappedChangeStatus = (value) => {
      const fn = handlerRef.current;
      if (typeof fn === "function") fn(value);
    };

    connection.off("ChangeStatus");
    connection.on("ChangeStatus", wrappedChangeStatus);

    connection.onreconnected(async () => {
      attempt = 0;
      try {
        await invokeAddToEvent();
      } catch (e) {
        console.error("AddToEvent after reconnect failed:", e);
      }
    });

    connection.onclose(() => {
      if (cancelled) return;
      scheduleReconnect();
    });

    const onVisibilityOrOnline = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        connectIfNeeded();
      }
    };

    connectIfNeeded();

    document.addEventListener("visibilitychange", onVisibilityOrOnline);
    window.addEventListener("online", onVisibilityOrOnline);

    intervalId = setInterval(() => {
      if (
        cancelled ||
        document.visibilityState !== "visible" ||
        connection.state !== HubConnectionState.Disconnected
      ) {
        return;
      }
      connectIfNeeded();
    }, 30000);

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerId);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityOrOnline);
      window.removeEventListener("online", onVisibilityOrOnline);
      connection.off("ChangeStatus", wrappedChangeStatus);
      connection.stop().catch(() => {});
    };
  }, [connection]);
}
